import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/feature/EmptyState";
import Pagination from "@/components/feature/Pagination";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  PAGE_SIZE,
  getPopularVideos,
  getTotalPages,
  paginate,
  searchVideos,
} from "@/utils/video";

const hotKeywords = ["夜色", "都市", "写真", "复古", "幕后", "雨夜", "光影"];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = searchParams.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(keyword);
    setPage(1);
  }, [keyword]);

  const results = useMemo(() => searchVideos(keyword), [keyword]);
  const totalPages = getTotalPages(results.length, PAGE_SIZE);
  const safePage = Math.min(page, totalPages);
  const pageItems = paginate(results, safePage, PAGE_SIZE);
  const hotVideos = useMemo(() => getPopularVideos(4), []);
  const hasQuery = keyword.trim().length > 0;

  useDocumentMeta({
    title: hasQuery
      ? `“${keyword}”搜索结果 - 亚洲激情视频`
      : "视频搜索 - 亚洲激情视频",
    description: hasQuery
      ? `亚洲激情视频为你找到与“${keyword}”相关的 ${results.length} 部示例片目，支持按标题、标签与分类匹配，点击可查看视频详情。`
      : "在亚洲激情视频搜索页输入关键词，按标题、标签与分类快速查找亚洲激情主题的示例视频内容。",
    keywords: hasQuery
      ? `${keyword},亚洲激情视频,视频搜索,${keyword}视频`
      : "亚洲激情视频,视频搜索,关键词搜索,视频资源",
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = input.trim();
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : "/search");
  };

  return (
    <SiteLayout>
      <section className="border-b border-background-800 bg-gradient-to-b from-primary-950/50 to-background-950">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
            搜索视频
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-400">
            输入关键词后按标题、标签与分类同时匹配。搜索结果由站内示例数据生成，仅用于演示搜索体验。
          </p>

          <form onSubmit={handleSubmit} className="mt-6 flex w-full max-w-2xl flex-col gap-3 sm:flex-row">
            <div className="flex h-11 w-full min-w-0 flex-1 items-center gap-2 rounded-md border border-background-700 bg-background-900 px-4 focus-within:border-primary-500">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-search-line text-base text-foreground-500" />
              </span>
              <input
                type="search"
                name="q"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="试试：夜色、都市、写真、幕后"
                aria-label="搜索关键词"
                className="h-full w-full border-0 bg-transparent text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none"
              />
              {input && (
                <button
                  type="button"
                  onClick={() => setInput("")}
                  aria-label="清空关键词"
                  className="flex h-4 w-4 cursor-pointer items-center justify-center text-foreground-500 hover:text-foreground-200"
                >
                  <i className="ri-close-circle-fill text-sm" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="h-11 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-500 sm:w-auto"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-600">热门关键词：</span>
            {hotKeywords.map((word) => (
              <Link
                key={word}
                to={`/search?q=${encodeURIComponent(word)}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  keyword === word
                    ? "border-primary-600 bg-primary-600/20 text-primary-200"
                    : "border-background-800 text-foreground-400 hover:border-primary-700 hover:text-primary-200"
                }`}
              >
                {word}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
        {hasQuery ? (
          <>
            <div className="flex flex-wrap items-baseline gap-2">
              <h2 className="font-heading text-base font-semibold text-foreground-100">
                “{keyword}” 的搜索结果
              </h2>
              <span className="text-xs text-foreground-500">共 {results.length} 条匹配</span>
            </div>

            <div className="mt-6">
              {results.length === 0 ? (
                <>
                  <EmptyState
                    icon="ri-search-eye-line"
                    title="没有找到匹配的视频"
                    description="换个关键词试试，比如只输入两个字，或者从下方的热门推荐里直接挑一部看看。"
                    actionText="查看全部热门"
                    onAction={() => navigate("/search")}
                  />

                  <div className="mt-12">
                    <h3 className="mb-4 font-heading text-base font-semibold text-foreground-100">
                      也许你会感兴趣
                    </h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      {hotVideos.map((video) => (
                        <VideoCard key={video.id} video={video} />
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {pageItems.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              )}
            </div>

            {results.length > 0 && (
              <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
            )}
          </>
        ) : (
          <>
            <div className="rounded-lg border border-background-800 bg-background-900 px-6 py-12 text-center">
              <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-background-800">
                <i className="ri-search-line text-2xl text-primary-400" />
              </span>
              <h2 className="font-heading text-lg font-semibold text-foreground-100">
                输入关键词开始搜索
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
                搜索会同时匹配视频标题、简介、标签与所属分类。也可以先看看站内热度最高的几部作品。
              </p>
            </div>

            <div className="mt-12">
              <h2 className="mb-4 font-heading text-base font-semibold text-foreground-100">
                近期热门视频
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {hotVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </>
        )}
      </section>
    </SiteLayout>
  );
}